/** `sections/impact-callout.liquid` — `<impact-callout>`: keeps the inner
 *  `.copy` block vertically centered in the viewport via sticky positioning.
 *  The element height changes with viewport width / font load, so we
 *  measure on connect, on resize, and whenever ResizeObserver fires.
 *
 *  Also drives a scroll-based parallax on the decorative `.hex` tiles in
 *  `.hex-frame`: each tile gets its own speed so they drift at slightly
 *  different rates as the section scrolls through the viewport. The offset is
 *  measured from the point where the section center meets the viewport center,
 *  so tiles sit at their design position when the section is centered.
 */

// Per-tile parallax speed (px of translate per px of section-center offset).
// Mixed signs so neighbouring tiles drift in opposite directions.
// `HEX_SPEEDS` drives desktop; `HEX_SPEEDS_MOBILE` drives ≤768px (matching the
// mobile breakpoint in impact-callout.scss). Tune each set independently.
const HEX_SPEEDS = {
  'hex-tl': 0.12,
  'hex-tr': -0.08,
  'hex-bl': -0.1,
  'hex-bc': 0.8,
  'hex-br': 0.09,
}

const HEX_SPEEDS_MOBILE = {
  'hex-tl': 0.05,
  'hex-tr': -0.06,
  'hex-bl': .2,
  'hex-bc': -0.05,
  'hex-br': 0.07,
}

const MOBILE_QUERY = '(max-width: 768px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

class ImpactCallout extends HTMLElement {
  #copy
  #resizeObserver
  #hexes = []
  #ticking = false
  #reducedMotion
  #mobileMql

  connectedCallback() {
    this.#copy = this.querySelector('.copy')
    if (!this.#copy) return

    this.#updateTop()

    this.#resizeObserver = new ResizeObserver(this.#updateTop)
    this.#resizeObserver.observe(this.#copy)

    window.addEventListener('resize', this.#updateTop)

    document.fonts?.ready?.then(this.#updateTop)

    this.#reducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY)
    this.#setupParallax()
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect()
    this.#resizeObserver = undefined
    window.removeEventListener('resize', this.#updateTop)

    window.removeEventListener('scroll', this.#onScroll)
    this.#reducedMotion?.removeEventListener?.('change', this.#onReducedMotionChange)
    this.#mobileMql?.removeEventListener?.('change', this.#onBreakpointChange)
  }

  #updateTop = () => {
    if (!this.#copy) return
    const halfHeight = this.#copy.offsetHeight / 2
    this.#copy.style.top = `calc(50vh - ${halfHeight}px)`
  }

  #setupParallax() {
    this.#hexes = Array.from(this.querySelectorAll('.hex')).map((el) => {
      const speedClass = Object.keys(HEX_SPEEDS).find((cls) => el.classList.contains(cls))
      return { el, speedClass }
    })

    if (this.#hexes.length === 0) return

    this.#mobileMql = window.matchMedia?.(MOBILE_QUERY)
    this.#mobileMql?.addEventListener?.('change', this.#onBreakpointChange)

    this.#reducedMotion?.addEventListener?.('change', this.#onReducedMotionChange)

    if (this.#reducedMotion?.matches) return

    window.addEventListener('scroll', this.#onScroll, { passive: true })
    this.#applyParallax()
  }

  #speeds() {
    return this.#mobileMql?.matches ? HEX_SPEEDS_MOBILE : HEX_SPEEDS
  }

  #onBreakpointChange = () => {
    if (this.#reducedMotion?.matches) return
    this.#applyParallax()
  }

  #onReducedMotionChange = () => {
    if (this.#reducedMotion?.matches) {
      window.removeEventListener('scroll', this.#onScroll)
      this.#resetParallax()
    } else {
      window.addEventListener('scroll', this.#onScroll, { passive: true })
      this.#applyParallax()
    }
  }

  #onScroll = () => {
    if (this.#ticking) return
    this.#ticking = true
    window.requestAnimationFrame(() => {
      this.#applyParallax()
      this.#ticking = false
    })
  }

  #applyParallax() {
    const viewportHeight = window.innerHeight
    const rect = this.getBoundingClientRect()

    // Skip work when the section is well outside the viewport.
    if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return

    // Offset of the section center from the viewport center; 0 when centered.
    const offset = rect.top + rect.height / 2 - viewportHeight / 2

    const speeds = this.#speeds()
    for (const { el, speedClass } of this.#hexes) {
      const speed = speeds[speedClass] ?? 0
      const translate = (offset * speed).toFixed(2)
      el.style.transform = `translate3d(0, ${translate}px, 0)`
    }
  }

  #resetParallax() {
    for (const { el } of this.#hexes) {
      el.style.transform = ''
    }
  }
}

if (!customElements.get('impact-callout')) {
  customElements.define('impact-callout', ImpactCallout)
}
